import React, { useState, useEffect } from 'react';
import { fetchFinancialYears, fetchRevenueStreams, 
    fetchOtherRevenues, saveOtherRevenue  } from '../services/revenueService';
import { fetchSubcounties } from '../services/subcountyService';
// import { } from '../services/revenuestreamService';

const MONTH_ORDER = ['July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March', 'April', 'May', 'June'];

const ManualRevenueManager = () => {
  // Section 1 State
  const [financialYears, setFinancialYears] = useState([]);
  const [subcounties, setSubcounties] = useState([]);
  
  const [selectedFY, setSelectedFY] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSubCounty, setSelectedSubCounty] = useState('');

  // Section 2 State (Lookup Configuration Lists)
  const [masterStreams, setMasterStreams] = useState([]); // Raw distinct combination rows
  const [filteredStreams, setFilteredStreams] = useState([]); // Distinct strings
  const [filteredSubStreams, setFilteredSubStreams] = useState([]); // Match sub streams
  
  const [selectedStream, setSelectedStream] = useState('');
  const [selectedSubStream, setSelectedSubStream] = useState('');

  // Section 3 State (Grid management / Entry mode)
  const [records, setRecords] = useState([]);
  const [computedStatus, setComputedStatus] = useState('open');
  const [inputAmount, setInputAmount] = useState('');
  const [inputDescription, setInputDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // 1. Core Metadata Lookup on Component Initialization
  useEffect(() => {
    const initData = async () => {
      try {
        const [fyList, scList, streamsData] = await Promise.all([
          fetchFinancialYears(),
          fetchSubcounties(),
          fetchRevenueStreams()
        ]);
        
        // Handle database array structures cleanly
        const parsedFYs = Array.isArray(fyList[0]) ? fyList[0] : (Array.isArray(fyList) ? fyList : []);
        setFinancialYears(parsedFYs);
        setSubcounties(scList);
        setMasterStreams(streamsData);

        // Process unique values for stream lookup dropdown
        const distinctStreams = [...new Set(streamsData.map(item => item.rev_stream))];
        setFilteredStreams(distinctStreams);

        // Compute Default Financial Year automatically matching todays date
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonthIdx = today.getMonth(); // 0 = Jan, 6 = July
        let defaultFY = `FY${currentYear}/${currentYear + 1}`;
        if (currentMonthIdx < 6) {
          defaultFY = `FY${currentYear - 1}/${currentYear}`;
        }
        
        // Check if matching string exists in loaded options collection
        const exists = parsedFYs.some(item => (item.financial_year || item) === defaultFY);
        if (exists) setSelectedFY(defaultFY);
      } catch (err) {
        console.error('Error executing initial config lookups:', err);
      }
    };
    initData();
  }, []);

  // 2. Compute available calendar months and years when Financial Year switches
  useEffect(() => {
    if (!selectedFY) {
      setSelectedYear('');
      setSelectedMonth('');
      return;
    }
    // Parse 'FY2025/2026' -> startYear = 2025, endYear = 2026
    const matches = selectedFY.match(/\d+/g);
    if (!matches || matches.length < 2) return;
    
    const startYear = matches[0];
    const endYear = matches[1];

    // If no year is chosen yet, select the startYear by default
    if (selectedYear !== startYear && selectedYear !== endYear) {
      setSelectedYear(startYear);
    }
  }, [selectedFY]);

  // 3. Keep Month select clean according to active Year values
  useEffect(() => {
    setSelectedMonth('');
  }, [selectedYear]);

  // 4. Calculate execution period processing parameters (Open/Closed status)
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-12 scale

    const monthMap = { July:6, August:7, September:8, October:9, November:10, December:11, January:0, February:1, March:1, April:2, May:3, June:4 };
    const targetMonthIdx = monthMap[selectedMonth];
    const targetYearNum = parseInt(selectedYear);

    // Evaluate date context: Past periods are instantly tagged 'closed'
    if (targetYearNum < currentYear || (targetYearNum === currentYear && targetMonthIdx < currentMonth)) {
      setComputedStatus('closed');
    } else {
      setComputedStatus('open');
    }
  }, [selectedYear, selectedMonth]);

  // 5. Cascade options filter for Revenue Streams dropdowns
  useEffect(() => {
    if (!selectedStream) {
      setFilteredSubStreams([]);
      setSelectedSubStream('');
      return;
    }
    const matches = masterStreams
      .filter(item => item.rev_stream === selectedStream)
      .map(item => item.rev_sub_stream);
    setFilteredSubStreams([...new Set(matches)]);
    setSelectedSubStream('');
  }, [selectedStream, masterStreams]);

  // 6. Query and pull existing data when selections are completed
  const loadRecords = async () => {
    if (!selectedFY || !selectedYear || !selectedMonth || !selectedSubCounty || !selectedStream) return;
    try {
      const data = await fetchOtherRevenues({
        financial_year: selectedFY,
        calendar_year: selectedYear,
        calendar_month: selectedMonth,
        sub_county_id: selectedSubCounty,
        rev_stream: selectedStream,
        rev_sub_stream: selectedSubStream
      });
      setRecords(data);
      setEditingId(null);
      setInputAmount('');
      setInputDescription('');
    } catch (err) {
      console.error('Error reading manual revenue table rows:', err);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [selectedFY, selectedYear, selectedMonth, selectedSubCounty, selectedStream, selectedSubStream]);

  // Handle Form Submission / Entry committing
  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!inputAmount || isNaN(inputAmount)) {
      setMsg({ type: 'error', text: 'Please input a valid numeric collection amount value.' });
      return;
    }
    
    // Require explicit sub stream specification if entering new records from scratch
    if (!selectedSubStream && records.length === 0) {
      setMsg({ type: 'error', text: 'Please explicitly select a revenue sub-stream before creating new records.' });
      return;
    }

    try {
      const payload = {
        rev_stream: selectedStream,
        rev_sub_stream: editingId ? records.find(r => r.id === editingId)?.rev_sub_stream : selectedSubStream,
        financial_year: selectedFY,
        sub_county_id: parseInt(selectedSubCounty),
        calendar_year: parseInt(selectedYear),
        calendar_month: selectedMonth,
        status: computedStatus,
        amount: parseFloat(inputAmount),
        description: inputDescription
      };

      await saveOtherRevenue(payload);
      setMsg({ type: 'success', text: 'Data saved successfully!' });
      loadRecords();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to commit revenue adjustments.' });
    }
  };

  const startEditMode = (row) => {
    setEditingId(row.id);
    setInputAmount(row.amount);
    setInputDescription(row.description || '');
  };

  // Check if Section 1 forms are fully populated
  const isSection1Complete = selectedFY && selectedYear && selectedMonth && selectedSubCounty;
  
  // Parse dynamic calendar years from FY selection
  const getFYYears = () => {
    const matches = selectedFY.match(/\d+/g);
    return matches && matches.length >= 2 ? [matches[0], matches[1]] : [];
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1000px', margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center', color: '#1e293b' }}>Manual Update of Revenues Data</h2>
      <h3 style={{ textAlign: 'center', color: '#1e293b' }}>(Revenue generated outside the current ICRMS)</h3>
      <h3 style={{ textAlign: 'center', color: '#1e293b' }}>==============================================</h3>
      
      {msg.text && (
        <div style={{ padding: '12px', marginBottom: '15px', borderRadius: '6px', backgroundColor: msg.type === 'error' ? '#fee2e2' : '#dcfce7', color: msg.type === 'error' ? '#991b1b' : '#166534', fontWeight: 'bold' }}>
          {msg.text}
        </div>
      )}

      {/* SECTION 1 */}
      <fieldset style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
        <legend style={{ fontWeight: 'bold', padding: '0 10px', color: '#475569' }}>Section 1: Period & Jurisdiction Selection</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Financial Year:</label>
            <select value={selectedFY} onChange={(e) => setSelectedFY(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose FY --</option>
              {financialYears.map((item, idx) => {
                const val = typeof item === 'object' ? item.financial_year : item;
                return <option key={idx} value={val}>{val}</option>;
              })}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Calendar Year:</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} disabled={!selectedFY} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose Year --</option>
              {getFYYears().map(yr => <option key={yr} value={yr}>{yr}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Calendar Month:</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} disabled={!selectedYear} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose Month --</option>
              {MONTH_ORDER.filter(m => {
                if (!selectedFY) return false;
                const years = getFYYears();
                if (selectedYear === years[0]) return ['July', 'August', 'September', 'October', 'November', 'December'].includes(m);
                if (selectedYear === years[1]) return ['January', 'February', 'March', 'April', 'May', 'June'].includes(m);
                return false;
              }).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Subcounty:</label>
            <select value={selectedSubCounty} onChange={(e) => setSelectedSubCounty(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose Sub County --</option>
              {subcounties.map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
            </select>
          </div>

        </div>
      </fieldset>

      {/* SECTION 2 */}
      <fieldset style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px', marginBottom: '20px' }} disabled={!isSection1Complete}>
        <legend style={{ fontWeight: 'bold', padding: '0 10px', color: !isSection1Complete ? '#94a3b8' : '#475569' }}>Section 2: Revenue Stream Allocation</legend>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Revenue Stream:</label>
            <select value={selectedStream} onChange={(e) => setSelectedStream(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose Stream --</option>
              {filteredStreams.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Revenue Sub-Stream:</label>
            <select value={selectedSubStream} onChange={(e) => setSelectedSubStream(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
              <option value="">-- Choose Sub-Stream (All) --</option>
              {filteredSubStreams.map(sub => <option key={sub} value={sub}>{sub}</option>)}
            </select>
          </div>

        </div>
      </fieldset>

      {/* SECTION 3 */}
      {isSection1Complete && selectedStream && (
        <fieldset style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '15px' }}>
          <legend style={{ fontWeight: 'bold', padding: '0 10px', color: '#475569' }}>Section 3: Review Records & Data Entry Panel</legend>
          
          <div style={{ marginBottom: '15px', fontSize: '14px', color: '#475569' }}>
            <strong>Inferred Period Status:</strong> <span style={{ padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: computedStatus === 'open' ? '#dcfce7' : '#fee2e2', color: computedStatus === 'open' ? '#15803d' : '#b91c1c' }}>{computedStatus}</span>
          </div>

          {records.length > 0 ? (
            <div>
              <p style={{ fontWeight: 'bold' }}>Existing Records Found:</p>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Sub Stream</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Amount (KES)</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '10px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(row => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px' }}>{row.rev_sub_stream}</td>
                      <td style={{ padding: '10px', fontWeight: '600' }}>{new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(row.amount)}</td>
                      <td style={{ padding: '10px', color: '#64748b' }}>{row.description || '—'}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button onClick={() => startEditMode(row)} style={{ padding: '4px 10px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#64748b', fontStyle: 'italic' }}>No records found for the matching collection keys. Provide values below to initialize data entry.</p>
          )}

          {/* Input / Editing Form Field Section */}
          {(records.length === 0 || editingId !== null) && (
            <form onSubmit={handleSubmitEntry} style={{ backgroundColor: '#f8fafc', padding: '15px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 10px 0' }}>{editingId ? 'Modify Selected Row Entry' : 'Create New External Revenue Entry'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Amount:</label>
                  <input type="number" step="0.01" value={inputAmount} onChange={(e) => setInputAmount(e.target.value)} placeholder="0.00" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '5px' }}>Description:</label>
                  <input type="text" value={inputDescription} onChange={(e) => setInputDescription(e.target.value)} placeholder="Notes regarding entry sources..." style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>Commit Record</button>
                {editingId && <button type="button" onClick={() => { setEditingId(null); setInputAmount(''); setInputDescription(''); }} style={{ padding: '8px 16px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>}
              </div>
            </form>
          )}

        </fieldset>
      )}
    </div>
  );
};

export default ManualRevenueManager;